import type { BrandHeadingFont } from "@/config/distribution";

/** Where `stageBrandFonts` in `scripts/whitelabel.mjs` puts the files it copies. */
const FONT_DIRECTORY = "/brand/fonts/";
const MARKER = "data-brand-font";
/** What `--font-heading` falls back to, so a font that fails to load still looks like the app. */
const FALLBACK = '"Poppins", ui-sans-serif, system-ui, sans-serif';

/**
 * The family name and every file name end up inside a quoted CSS string, so
 * anything that could close that string, or the `url()` around it, is refused
 * rather than escaped: a brand manifest is not a place to write CSS.
 */
function quotable(value: string | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed && !/["'\\;{}<>()]/.test(trimmed) ? trimmed : null;
}

function faceRule(family: string, weight: number | undefined, file: string): string {
  return [
    "@font-face {",
    `  font-family: "${family}";`,
    "  font-style: normal;",
    `  font-weight: ${weight ?? 400};`,
    "  font-display: swap;",
    `  src: url("${FONT_DIRECTORY}${file}") format("woff2");`,
    "}",
  ].join("\n");
}

/**
 * Applies the brand's heading font before Vue renders: one `<style>` with the
 * `@font-face` rules for the files the build staged, or one `<link>` to the
 * foundry stylesheet, plus `--font-heading` on the root element.
 *
 * A brand without a font changes nothing, which is every Coachio build: the
 * property then keeps the Poppins default from `theme/variables.scss`.
 * Returns whether a font was applied.
 */
export function applyBrandFont(
  font: BrandHeadingFont,
  doc: Document = document,
): boolean {
  const family = quotable(font?.family);
  const named = (font?.files ?? []).filter((file) => Boolean(file?.trim()));
  const files = named.map(quotable);
  const stylesheet = font?.stylesheet?.trim();
  // One unusable name means the whole block is not to be trusted, so nothing is applied
  // rather than a font that silently misses a weight or a subset.
  if (!family || files.some((file) => file === null)) return false;
  if (!files.length && !stylesheet) return false;

  doc.head.querySelectorAll(`[${MARKER}="heading"]`).forEach((node) => node.remove());

  let node: HTMLElement;
  if (files.length) {
    node = doc.createElement("style");
    node.textContent = files.map((file) => faceRule(family, font.weight, file as string)).join("\n");
  } else {
    node = doc.createElement("link");
    node.setAttribute("rel", "stylesheet");
    node.setAttribute("href", stylesheet as string);
  }
  node.setAttribute(MARKER, "heading");
  doc.head.appendChild(node);

  doc.documentElement.style.setProperty("--font-heading", `"${family}", ${FALLBACK}`);
  return true;
}
